/**
 * Export TinyMCE document HTML to a .docx file (client-side).
 */

import { asBlob } from 'html-docx-js-typescript';
import { applyTableFigureListsToExportHtml } from '../utils/tableFigureListUtils';
import { stripExportOmitFromHtml } from './pdfExportService';

const WORD_EXPORT_STYLES = `
body {
  font-family: 'Times New Roman', Times, serif;
  font-size: 12pt;
  line-height: 2;
  color: #000;
}
p { margin: 0; text-indent: 0.5in; }
p.wf-no-indent, .mce-toc p, .wf-toc-tip p, td p, th p, li p,
blockquote p, h1 + p, h2 + p, h3 + p, h4 + p, h5 + p, h6 + p,
div.page-break-before > p:first-of-type { text-indent: 0; }
blockquote { margin: 0.5em 0 0.5em 0.5in; font-size: 12pt; line-height: 2; border-left: none; padding-left: 0; }
h1, h2, h3, h4, h5, h6 { font-weight: bold; }
h1 { text-align: center; font-size: 12pt; }
h2 { font-size: 12pt; }
table { border-collapse: collapse; width: 100%; }
td, th { border: 1px solid #000; padding: 4px; vertical-align: top; }
table.wf-borderless, table.wf-borderless td, table.wf-borderless th { border: none; }
.wf-figure-caption, .wf-table-caption { font-style: italic; text-indent: 0; }
.page-break-before, .mce-pagebreak { page-break-before: always; }
`;

function escapeHtml(text = '') {
	return String(text)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function buildWordHtmlDocument(bodyHtml, title = 'document') {
	const safeTitle = escapeHtml(title);
	return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${safeTitle}</title>
  <style>${WORD_EXPORT_STYLES}</style>
</head>
<body>
${bodyHtml}
</body>
</html>`;
}

function triggerDownload(blob, fileName) {
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	link.rel = 'noopener';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

/**
 * @param {object} opts
 * @param {string} opts.contentHtml - HTML from TinyMCE
 * @param {string} opts.title - Document title (filename)
 * @returns {Promise<{ blob: Blob, save: (name?: string) => void }>}
 */
export async function exportDocumentToWord({ contentHtml, title = 'document' }) {
	const cleanedHtml = stripExportOmitFromHtml(contentHtml || '');
	const enrichedHtml = applyTableFigureListsToExportHtml(cleanedHtml);
	const fullHtml = buildWordHtmlDocument(enrichedHtml, title);
	const blob = await asBlob(fullHtml, {
		orientation: 'portrait',
		margins: { top: 1440, right: 1440, bottom: 1440, left: 1440 }
	});
	const fileName = `${title || 'document'}.docx`;

	return {
		blob,
		save: (name = fileName) => triggerDownload(blob, name || fileName)
	};
}
